import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"
import { useNativePlatform } from "@/hooks/useNativePlatform"
import { overlayZClass, sheetSurfaceZClass } from "@/lib/native/nativeZIndex"
import { nativeSheetBottomPad } from "@/lib/native/nativeSheetPadding"
import { useNativeSheetSwipe } from "@/lib/native/useNativeSheetSwipe"
import {
  NativeSheetDismissProvider,
  useNativeSheetDismissContext,
} from "@/lib/native/nativeSheetDismissContext"
import { cleanupNativeSheetChrome } from "@/lib/native/nativeSheetCleanup"
import { useNativeSheetEnterStabilizer } from "@/lib/native/useNativeSheetEnterStabilizer"

const SheetOpenContext = React.createContext(true)

const Sheet = ({
  onOpenChange,
  open: openProp,
  defaultOpen,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) => {
  const { isNative } = useNativePlatform()
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(
    () => openProp ?? defaultOpen ?? false,
  );
  const open = isControlled ? openProp : internalOpen;

  const finishClose = React.useCallback(() => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    onOpenChange?.(false);
    if (isNative) {
      requestAnimationFrame(() => cleanupNativeSheetChrome());
    }
  }, [isControlled, onOpenChange, isNative]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next) {
        if (!isControlled) {
          setInternalOpen(true);
        }
        onOpenChange?.(true);
        return;
      }
      if (!isNative) {
        if (!isControlled) {
          setInternalOpen(false);
        }
        onOpenChange?.(false);
      }
    },
    [isControlled, onOpenChange, isNative],
  );

  return (
    <NativeSheetDismissProvider onRequestClose={finishClose}>
      <SheetOpenContext.Provider value={open}>
        <SheetPrimitive.Root
          open={open}
          onOpenChange={handleOpenChange}
          {...props}
        />
      </SheetOpenContext.Provider>
    </NativeSheetDismissProvider>
  );
}

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const { isNative } = useNativePlatform()
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0",
        isNative
          ? `${overlayZClass(true)} bg-black/55`
          : `${overlayZClass(false)} bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
        className,
      )}
      {...props}
      ref={ref}
    />
  )
})
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  // Elevator rise — smooth ease-out, visible travel without feeling sluggish
  "fixed gap-4 bg-background p-6 shadow-lg transition ease-[cubic-bezier(0.2,0.7,0.2,1)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-280 data-[state=open]:duration-620",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top rounded-b-2xl max-h-[70dvh] overflow-y-auto",
        bottom:
          "inset-x-0 bottom-0 left-0 right-0 w-full max-w-none border-t rounded-t-2xl max-h-[70dvh] mx-0 flex flex-col overflow-hidden overscroll-y-contain data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm rounded-r-2xl",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm rounded-l-2xl",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  /** Native bottom sheet: swipe up to expand to near-full screen. */
  expandable?: boolean;
  /** When expanded, pull-down dismisses (no collapse step). */
  dismissWhenExpanded?: boolean;
  /** Stack above an open parent dialog/sheet (option pickers). */
  nestedSheet?: boolean;
  /** Extra classes for the dim overlay (e.g. raise above a high z-index shell). */
  overlayClassName?: string;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, expandable = false, dismissWhenExpanded = false, nestedSheet = false, overlayClassName, ...props }, ref) => {
  const { isNative } = useNativePlatform()
  const nativeBottom = isNative && side === "bottom"
  const dismissCtx = useNativeSheetDismissContext()
  const sheetOpen = React.useContext(SheetOpenContext)
  const effectiveExpandable = expandable === true && nativeBottom
  const effectiveCompact = nativeBottom && !effectiveExpandable
  const enterStable = useNativeSheetEnterStabilizer(nativeBottom && sheetOpen)
  const sheetSwipe = useNativeSheetSwipe({
    enabled: nativeBottom,
    expandable: effectiveExpandable,
    dismissWhenExpanded: effectiveExpandable && dismissWhenExpanded,
    sheetOpen,
  })
  const nestedOverlayZ = nestedSheet ? "z-[230]" : overlayZClass(isNative)
  const nestedSurfaceZ = nestedSheet ? "z-[231]" : sheetSurfaceZClass(isNative)
  return (
  <SheetPortal>
    <SheetOverlay
      className={cn(
        enterStable,
        nativeBottom && "native-overlay-stable",
        nestedSheet && nestedOverlayZ,
        overlayClassName,
      )}
      {...(nativeBottom ? { onPointerDown: sheetSwipe.backdropPointerDown } : {})}
    />
    <SheetPrimitive.Content
      ref={nativeBottom ? sheetSwipe.mergeSheetRef(ref) : ref}
      className={cn(
        sheetVariants({ side }),
        !nestedSheet && sheetSurfaceZClass(isNative),
        nestedSheet && nestedSurfaceZ,
        nativeBottom &&
          "native-bottom-sheet gap-0 p-0 will-change-transform flex flex-col overflow-hidden pointer-events-auto",
        effectiveExpandable && "native-sheet-expandable",
        effectiveCompact && "native-sheet-compact",
        nestedSheet && "native-nested-sheet",
        enterStable,
        // Expandable full sheets (e.g. För dig annons): no outer safe-area pad —
        // contained bidding bar paints into the inset. Compact sheets keep pad.
        nativeBottom && !effectiveExpandable && nativeSheetBottomPad,
        nativeBottom &&
          "!inset-x-0 !left-0 !right-0 !w-full !max-w-none !mx-0",
        className,
      )}
      {...(nativeBottom
        ? {
            onPointerDownOutside: sheetSwipe.blockRadixDismiss,
            onInteractOutside: sheetSwipe.blockRadixDismiss,
          }
        : {})}
      {...props}
    >
      {nativeBottom && sheetSwipe.dragHandle}
      <div
        className={cn(
          nativeBottom && "native-sheet-body flex-1 min-h-0 overscroll-contain px-6",
          nativeBottom &&
            (effectiveExpandable
              ? "flex h-full min-h-0 flex-col overflow-hidden !pb-0 !px-0"
              : "overflow-y-auto pb-6 pb-[calc(1.25rem+12px+env(safe-area-inset-bottom,0px))]"),
        )}
      >
        {children}
      </div>
      {nativeBottom && (
        <button
          type="button"
          className="absolute right-3 top-3 rounded-full opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-[101] h-9 w-9 flex items-center justify-center bg-background/90 backdrop-blur-sm border border-border shrink-0 pointer-events-auto touch-manipulation"
          aria-label="Close"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            dismissCtx?.requestClose();
          }}
        >
          <X className="h-4 w-4" />
        </button>
      )}
      {!nativeBottom && (
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet, SheetClose,
  SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger
}

